import React, { useState, ChangeEvent, FC, useEffect, useRef } from "react";
import PropagateLoader from "react-spinners/PropagateLoader";
import { extractObjectName } from "../../../utilities/functions";
import { translate } from "pdf-lib";
import { useTranslate } from "../../../hooks/LanguageProvider";
import TooltipText from "../../TooltipText";

interface Alarm {
  searchByPoi?: string;
  searchFieldPoi?: string;
  searchValuePoi?: string;
  subclass?: string;
  totalPoints?: number;
  ids?: number[];
}

interface PoiAlarmProps {
  alarm: any;
  onChange: (newMetrics: Alarm) => void;
}

interface PoiResult {
  idgeof: number;
  codegeof?: string;
  namegeof: string;
  descriptiongeof?: string;
  attributesgeof?: string;
  areageof: string;
  datecreategeof: string;
  user_id: number;
  intrash: number;
  etatgeof?: string;
  numPoints?: number;
  classgeof?: string;
  typegeof?: string;
}

const PoiAlarm: FC<PoiAlarmProps> = ({ alarm, onChange }) => {
  const [searchByPoi, setSearchByPoi] = useState("0");
  const [searchFieldPoi, setSearchFieldPoi] = useState("");
  const [searchValuePoi, setSearchValuePoi] = useState("");
  const [subclass, setSubclass] = useState("");
  const [totalPoints, setTotalPoints] = useState(0);
  const [poiResults, setPoiResults] = useState<PoiResult[]>([]);
  const [pois, setPois] = useState<PoiResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingList, setLoadingList] = useState(true);

  const [showDiv, setShowDiv] = useState(false);
  const id_user = localStorage.getItem("userid");
  const divRef = useRef<HTMLDivElement>(null);
  const { translate } = useTranslate();
  const [selectedPois, setSelectedPois] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  const [displayedPois, setDisplayedPois] = useState<PoiResult[]>([]);

  const [confirmedPois, setConfirmedPois] = useState<PoiResult[]>([]);

 const handleCheckboxChange = (poi: PoiResult) => {
  const id = poi.idgeof.toString();
  const isSelected = selectedPois.includes(id);

  if (isSelected) {
    const updatedSelected = selectedPois.filter((x) => x !== id);
    const updatedConfirmed = confirmedPois.filter((p) => p.idgeof.toString() !== id);

    setSelectedPois(updatedSelected);
    setConfirmedPois(updatedConfirmed);

    onChange({
      ...alarm,
      ids: updatedConfirmed.map((p) => p.idgeof),
      totalPoints: updatedConfirmed.length,
    });
  } else {
    const updatedSelected = [...selectedPois, id];
    const updatedConfirmed = [...confirmedPois, poi];

    setSelectedPois(updatedSelected);
    setConfirmedPois(updatedConfirmed);

    onChange({
      ...alarm,
      ids: updatedConfirmed.map((p) => p.idgeof),
      totalPoints: updatedConfirmed.length,
    });
  }
};



useEffect(() => {
  setSelectedPois(pois.map((p) => p.idgeof.toString()));
}, [pois]);

  const isChecked = (poiId: string) => selectedPois.includes(poiId);

  //"
  const handleSelectAllChange = () => {
    setSelectAll((prev) => !prev);
    if (!selectAll) {
      const allPois = pois.filter(
        (poi) => !selectedPois.includes(poi.idgeof.toString())
      ); // POIs non sélectionnés
      allPois.forEach((poi) => addPoiToList(poi)); // Ajout de tous les POIs non sélectionnés
    } else {
      selectedPois.forEach((poiId) => removePoiFromList(poiId)); // Retirer les POIs sélectionnés
    }
  };

  // Vérifie si tous les POIs sont sélectionnés
  useEffect(() => {
    setSelectAll(selectedPois.length === pois.length);
  }, [selectedPois, pois]);

  useEffect(() => {

    if (alarm) {
      setSearchByPoi(alarm.searchByPoi || "0");
     // setSearchFieldPoi(alarm.searchFieldPoi || "");
      setSearchValuePoi(alarm.searchValuePoi || "");
      setSubclass(alarm.subclass || "");
      setTotalPoints(alarm.totalPoints || 0);
    }
  }, [alarm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (divRef.current && !divRef.current.contains(event.target as Node)) {
        setShowDiv(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const loadSearchPois = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setSearchByPoi(value);
    onChange({ ...alarm, searchByPoi: value });
    // Hide the div when changing search criteria
    if (!searchFieldPoi.trim()) {
      setShowDiv(false);
    }
  };

  const handleSearchFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchFieldPoi(value);
    onChange({ ...alarm, searchFieldPoi: value });
    setSearchValuePoi(value);
    searchPois(value);
  };

  const searchPois = async (searchValue: string) => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(
        `${backendUrl}/api/search/${searchByPoi}/${searchValue}/${id_user}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const POIS = await response.json();
      const result: PoiResult[] = POIS.data;
      setPoiResults(result);
      setTotalPoints(result.length);

      // Show or hide the div based on POI results and search field
      if (searchValue.trim() === "") {
        setShowDiv(false);
      } else {
        setShowDiv(result.length > 0 || loading);
      }
    } catch (error) {
      console.error("Error fetching POIs:", error);
    } finally {
      setLoading(false);
    }
  };


  const getPois = async () => {
  setLoadingList(true);
  try {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    const response = await fetch(`${backendUrl}/api/poi/finds`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id_pois: alarm.ids,
        id_user: id_user,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result: PoiResult[] = await response.json();

    // ✅ Initialiser les trois listes distinctes
    setDisplayedPois(result); // pour affichage
    setConfirmedPois(result); // pour alarm.ids et totalPoints
    setSelectedPois(result.map((poi) => String(poi.idgeof))); // cases cochées

    // ✅ Appel explicite à onChange avec les POIs confirmés
    onChange({
      ...alarm,
      ids: result.map((poi) => poi.idgeof),
      totalPoints: result.length,
    });
  } catch (error) {
    console.error('Error fetching POIs:', error);
    setDisplayedPois([]);
    setConfirmedPois([]);
    setSelectedPois([]);
  } finally {
    setLoadingList(false);
  }
};


 useEffect(() => {
  if (alarm?.ids?.length > 0 && id_user) {
    getPois();
  }
}, []);
  const addPoiToList = (poi: PoiResult) => {
    // Check if the POI is already in the list
    if (!pois.find((p) => p.idgeof === poi.idgeof)) {
      const updatedPois = [...pois, poi];
      setPois(updatedPois);

      // Update the metrics with the new list of POIs
      onChange({
        ...alarm,
        ids: updatedPois.map((p) => p.idgeof),
        totalPoints: updatedPois.length,
      });
    }
  };

  const PoiToList = (poi: PoiResult) => {
    if (!pois.some((p) => p.idgeof === poi.idgeof)) {
      setPois((prev) => [...prev, poi]);
    }
  };

  // Fonction pour retirer un POI de la liste
  const removePoiFromList = (poiId: string) => {
    const updatedPois = pois.filter((p) => p.idgeof.toString() !== poiId); // Retire le POI de la liste
    setPois(updatedPois); // Met à jour la liste des POIs

    // Mise à jour des métriques avec la nouvelle liste des POIs
    onChange({
      ...alarm,
      ids: updatedPois.map((p) => p.idgeof),
      totalPoints: updatedPois.length,
    });
  };

const handlePoiClick = (poi: PoiResult) => {
  if (!confirmedPois.find((p) => p.idgeof === poi.idgeof)) {
    const updated = [...confirmedPois, poi];
    setConfirmedPois(updated);

    onChange({
      ...alarm,
      ids: updated.map((p) => p.idgeof),
      totalPoints: updated.length,
    });
  }

  // Rendre visible dans le tableau si pas encore affiché
  if (!displayedPois.find((p) => p.idgeof === poi.idgeof)) {
    setDisplayedPois((prev) => [...prev, poi]);
  }

  setSelectedPois((prev) => [...prev, poi.idgeof.toString()]);
  setShowDiv(false);
};

  /***********************  Speed Alarm   ************************/
  interface SpeedAlarmProps {
    alarm: any; // Adapter le type en fonction de votre structure d'alarme
    onChange: (newMetrics: any) => void; // Fonction de rappel pour mettre à jour l'état du composant parent
  }


  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    // Implémentation pour cocher toutes les lignes
  };


  const listRef = useRef<HTMLDivElement>(null);


  useEffect(() => {
  const handleClickOutside = (event: MouseEvent) => {
    if (listRef.current && !listRef.current.contains(event.target as Node)) {
      setShowDiv(false); // Fermer la liste si clic à l'extérieur
    }
  };

  document.addEventListener("mousedown", handleClickOutside);
  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, []);



  return (
    <div className="col-md-12" id="extra_cell" style={{ marginTop: "10px" }}>
      <div className="row" id="alarm_4_2">
        <div
          className="col-md-2 col-sm-4"
          style={{ marginTop: "2px", padding: "0px" }}
        >
          <div className="input-group input-group-sm">
            <span className="input-group-text">{translate("Search by")}</span>
            <select
              className="form-select"
              name="search_by_poi"
              value={searchByPoi}
              onChange={loadSearchPois}
            >
              <option value="36">{translate("Nom")}</option>
              <option value="37">{translate("Code")}</option>
            </select>
          </div>
        </div>

        <div
          className="col-md-2 col-sm-4"
          id="search_box_poi"
          style={{ marginTop: "2px" }}
        >
          <div className="input-group input-group-sm">
            <input
              className="form-control ui-autocomplete-input"
              type="text"
              id="search_field_poi"
              name="search_field_poi"
              placeholder={translate("Search") + "..."}
              value={searchFieldPoi}
              onChange={handleSearchFieldChange}
              autoComplete="off"
            />
            <input
              type="hidden"
              name="search_value_poi"
              value={searchValuePoi}
              disabled
            />
            <span className="input-group-text">
              <i className="fa fa-search"></i>{" "}
            </span>
          </div>
        </div>

    

        <div className="col-md-6 col-sm-6">
          <div
            style={{
              marginTop: "2px",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: "16px", // espace entre bouton et total
            }}
          >
            {/* Total Points */}
            <div className="text-muted small">
              <span>{translate("Total points")}: </span>
              <span id="total_points">{totalPoints}</span>
            </div>

            {/* Bouton Ajouter */}
            <a
              className="btn btn-default btn-sm gp"
              href="/poi/add"
              title="Ajouter"
              data-toggle="tooltip"
              target="_blank"
              style={{ border: "1px solid #ddd" }}
            >
              <i className="fas fa-map-pin"></i> {translate("Create POI")}
            </a>
          </div>
        </div>
      </div>

      {showDiv && (
        <div className="row mt-2">
          <div className="col-md-12">
            <div
              ref={listRef}
              id="alarm_4"
              className="autocomplete-items"
              style={{
                backgroundColor: "#fff",
                height: "250px",
                overflowY: "auto",
                padding: "5px",
                display: "flex",
                justifyContent: loading ? "center" : "flex-start",
                alignItems: loading ? "center" : "flex-start",
              }}
            >
              {loading ? (
                <PropagateLoader color="#123abc" loading size={20} />
              ) : poiResults.length > 0 ? (
                <table
                  id="poi_table"
                  className="table table-hover table-sm mb-0"
                  style={{
                    borderCollapse: "separate",
                    borderSpacing: "0px",
                    width: "100%",
                  }}
                >
                  <thead>
                    <tr style={{ borderBottom: "1px solid #e0e0e0" }}>
                      <th style={{ borderRight: "none", padding: "4px 8px" }}>ID POI</th>
                      <th style={{ borderRight: "none", padding: "4px 8px" }}>{translate("Name")}</th>
                      <th style={{ borderRight: "none", padding: "4px 8px" }}>{translate("Code")}</th>
                      <th style={{ borderRight: "none", padding: "4px 8px" }}>{translate("Description")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {poiResults.map((poi) => (
                      <tr
                        key={poi.idgeof}
                        style={{
                          cursor: "pointer",
                          borderBottom: "1px solid #eee", // ✅ bordure horizontale
                        }}
                        onClick={() => handlePoiClick(poi)}
                      >
                        <td style={{ borderRight: "none", padding: "4px 8px" }}>{poi.idgeof}</td>
                        <td style={{ borderRight: "none", padding: "4px 8px" }}>
                          {poi.namegeof}
                        </td>
                        <td style={{ borderRight: "none", padding: "4px 8px" }}>{poi.codegeof}</td>
                        <td style={{ borderRight: "none", padding: "4px 8px" }}>{poi.descriptiongeof}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : searchFieldPoi.trim() ? (
                <p className="m-0">{translate("No POI found")}</p>
              ) : null}
            </div>
          </div>
        </div>
      )}


      <div className="row p-0">
        <div className="table-responsive p-0">
          <table className="table dataTable">
            <thead className="bg-white text-uppercase">
              <tr className="ligth ligth-data">
                <th>
                  <div className="checkbox d-inline-block">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="selectAllPOI"
                      checked={selectAll}
                      onChange={handleSelectAllChange}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="selectAllPOI"
                    ></label>
                  </div>
                </th>
                <th>ID {translate("POI")}</th>
                <th>{translate("Name")}</th>
                <th>{translate("Code")}</th>
                <th>{translate("Description")}</th>
              </tr>
            </thead>
            <tbody className="ligth-body">
              {loadingList ? (
                <tr style={{ textAlign: "center" }}>
                  <td className="text-center" colSpan={5}>
                    <p>
                      <PropagateLoader
                        color={"#123abc"}
                        loading={loading}
                        size={20}
                      />
                    </p>
                  </td>
                </tr>
              ) : displayedPois.length > 0 ? (
                displayedPois.map((poi) => (
                  <tr key={poi.idgeof}>
                    <td>
                      <div className="checkbox d-inline-block">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={`poi_${poi.idgeof}_cb`}
                          name="poi_id"
                          value={poi.idgeof}
                          data-numpts={poi.numPoints}
                          checked={isChecked(poi.idgeof.toString())}
                          onChange={() => handleCheckboxChange(poi)}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`poi_${poi.idgeof}_cb`}
                        ></label>
                      </div>
                    </td>
                    <td>{poi.idgeof}</td>
                    <td>{poi.namegeof}</td>
                    <td>{poi.codegeof}</td>
                    <td>{poi.descriptiongeof}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} style={{ textAlign: "center" }}>
                    {translate("No associated POI")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PoiAlarm;
